import { useCallback, useRef } from 'react';

import { errorMessagesListFromResponse } from '../helpers/errors';
import type { FailedFetchResponse } from '../helpers/errors';

import type { AsyncValueWithActions } from './useAsyncValue';

/**
 * Connects a request to an AsyncValue, automatically handling updating the async value, handling error responses and
 * discarding stale requests.  Multiple callbacks can be connected to the same async value, but concurrent requests will
 * honor only the last response.
 *
 * The callback returned will change whenever the request changes.  Be sure to pass a stable request value if you want
 * this callback to be stable.
 *
 * @deprecated - Prefer to use the `useQuery`/`useMutation` hooks from `./app/ReactQuery/` instead.
 */
export default function useRequestConnectedToAsyncValue<T>(
  request: (...args: unknown[]) => Promise<{ data?: T }>,
  { setPending, setReject, setResolve }: AsyncValueWithActions<T>,
) {
  // Create a stable mutable object to track the timestamp of the request so we can discard stale responses.
  const requestTimestampRef = useRef<number>(0);
  return useCallback(
    async (...args: unknown[]) => {
      setPending();

      // Store the timestamp of this request so we can check it later.
      const requestTime = performance.now();
      requestTimestampRef.current = requestTime;

      try {
        const response = await request(...args);

        // If the request time has changed, then we've made another request and this one is no longer valid.
        if (requestTimestampRef.current !== requestTime) {
          return { data: null };
        }
        //  Response data could be null for cases where we are simply making a PUT/POST request.
        if (response?.data === undefined) {
          setReject(['No data found']);
          return { data: null };
        }
        setResolve(response.data);
        return response;
      } catch (error) {
        // `errorMessagesListFromResponse returns `APIError` types which are effectively strings but aren't equal to
        // strings.  Unwrapping here better matches the declared types and breaks the dependence on that abstraction so
        // that we can remove it later.
        setReject(errorMessagesListFromResponse(error as FailedFetchResponse).map((e) => e.toString()));
        return { data: null };
      }
    },
    [setPending, setResolve, setReject, request, requestTimestampRef],
  );
}
